import {
  ANGLE_DIGIT_STEPS,
  formatAngleDisplay,
  type AngleDigitIndex,
  uiDegreesToUnits,
  unitsToUiDegrees,
} from '../shared/angle.js';

/**
 * Digit-precision angle editor (0.00001° integer units).
 * Digit 0 = 1°, …, digit 5 = 0.00001°.
 */
export class AngleControl {
  units = 0;
  selectedDigit: AngleDigitIndex = 0;
  private listeners = new Set<(units: number) => void>();

  onChange(cb: (units: number) => void): void {
    this.listeners.add(cb);
  }

  private emit(): void {
    for (const cb of this.listeners) cb(this.units);
  }

  setUnits(units: number): void {
    this.units = uiDegreesToUnits(unitsToUiDegrees(units));
    this.emit();
  }

  selectDigitDelta(delta: number): void {
    let d = this.selectedDigit + delta;
    if (d < 0) d = 0;
    if (d > 5) d = 5;
    this.selectedDigit = d as AngleDigitIndex;
  }

  nudge(direction: 1 | -1): void {
    const step = ANGLE_DIGIT_STEPS[this.selectedDigit];
    this.units = uiDegreesToUnits(unitsToUiDegrees(this.units + direction * step));
    this.emit();
  }

  /**
   * Shift+arrow: jump by 5°, always landing on a multiple of 5.00000°
   * (degrees ending in 0 or 5).
   */
  nudgeShift(direction: 1 | -1): void {
    const deg = unitsToUiDegrees(this.units);
    let next: number;
    if (direction > 0) {
      const grid = Math.floor(deg / 5 + 1e-9) * 5;
      next = Math.abs(deg - grid) < 1e-9 ? deg + 5 : grid + 5;
    } else {
      const grid = Math.ceil(deg / 5 - 1e-9) * 5;
      next = Math.abs(deg - grid) < 1e-9 ? deg - 5 : grid - 5;
    }
    this.units = uiDegreesToUnits(next);
    this.selectedDigit = 0; // jump cursor to integer place
    this.emit();
  }

  /**
   * Fixed layout: DDD.FFFFF° — each digit is its own cell so width never jumps.
   * Integer places: hundreds / tens / ones (selectedDigit 0 = ones).
   */
  renderHtml(): string {
    const text = formatAngleDisplay(this.units, '.');
    const [intPart, fracPart = '00000'] = text.split('.');
    const intDigits = intPart.padStart(3, '0').slice(-3).split('');
    const fracDigits = (fracPart + '00000').slice(0, 5).split('');

    const cell = (ch: string, selected: boolean) =>
      `<span class="digit-cell${selected ? ' selected' : ''}"><input class="digit-input" type="text" maxlength="1" readonly tabindex="-1" value="${ch}" aria-hidden="true"></span>`;

    let html = '';
    // hundreds, tens, ones — only ones is the "integer digit" selection target
    html += cell(intDigits[0], false);
    html += cell(intDigits[1], false);
    html += cell(intDigits[2], this.selectedDigit === 0);
    html += `<span class="sep">.</span>`;
    for (let i = 0; i < 5; i++) {
      html += cell(fracDigits[i], this.selectedDigit === i + 1);
    }
    html += `<span class="unit">°</span>`;
    return html;
  }
}
